/* eslint-disable @typescript-eslint/no-explicit-any */
// src\pages\dashboard\Profile.tsx - UPDATED VERSION WITH SKELETON LOADING
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit2,
  Eye,
  EyeOff,
  Mail,
  Phone,
  MapPin,
  User,
  Briefcase,
  Bookmark,
  BriefcaseIcon,
  MoreVertical,
  Calendar,
  Users,
  Trash2,
  AlertTriangle,
  Shield,
  ShieldOff,
  Send,
  CheckCircle,
  XCircle,
  X,
  AlertCircle,
  FileText,
  Zap,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useDarkMode } from "../../context/DarkModeContext";
import ResendVerificationModal from "../../components/ResendVerificationModal";
import type { Subscription } from "../../types/auth";

// Import Skeleton components
import {
  // Skeleton,
  // SkeletonText,
  // SkeletonCard,
  // SkeletonList,
  // SkeletonJobCard,
  // SkeletonJobListing,
  SkeletonDashboard,
} from "../../components/Skeleton";
import Skeleton from "../../components/Skeleton";

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
  location: string;
  bio: string;
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default function Profile() {
  const {
    user,
    updateUserProfile,
    updateProfile,
    bookmarks,
    removeBookmark,
    fetchBookmarks,
    myJobs,
    fetchMyJobs,
    deleteAccount,
    deactivateAccount,
    hasActiveJobPostingSubscription,
    hasActiveWhitelistSubscription,
    canPostJobs,
    getRemainingJobPosts,
    syncSubscriptionState,
    loading: authLoading,
    getBookmarkStats,
    getJobStats,
    clearAllBookmarks,
    clearAllJobs,
    syncBookmarksToServer,
    syncJobsToServer,
    removeJob,
    apiRequest,
  } = useAuth();

  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [activeTab, setActiveTab] = useState<
    "profile" | "bookmarks" | "myJobs"
  >("profile");
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [showJobMenu, setShowJobMenu] = useState<string | null>(null);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [showDeactivateAccount, setShowDeactivateAccount] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState("");
  const [showDeactivatePassword, setShowDeactivatePassword] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showCancelSubscription, setShowCancelSubscription] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [syncingData, setSyncingData] = useState(false);

  // NEW: Loading states for bookmarks and jobs
  const [loadingBookmarks, setLoadingBookmarks] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState({
    bookmarks: false,
    myJobs: false,
  });

  const [formData, setFormData] = useState<ProfileFormData>({
    name: user?.name || "",
    email: user?.email || "",
    phone: (user?.profiles || user?.profile)?.phone || "",
    location: (user?.profiles || user?.profile)?.location || "",
    bio: (user?.profiles || user?.profile)?.bio || "",
  });

  // NEW: Refs to track API call state and prevent duplicates
  const isFetching = useRef(false);
  const lastFetchTime = useRef<number>(0);
  const lastTabChangeTime = useRef<number>(0);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE;

  // NEW: Debug useEffect to see current state
  useEffect(() => {
    console.log("🔍 Current bookmarks state:", bookmarks);
    console.log("🔍 Current myJobs state:", myJobs);
    console.log("🔍 Active tab:", activeTab);
  }, [bookmarks, myJobs, activeTab]);

  // Get stats from AuthContext
  const bookmarkStats = getBookmarkStats
    ? getBookmarkStats()
    : { total: bookmarks?.length || 0, recent: 0 };
  const jobStats = getJobStats
    ? getJobStats()
    : { total: myJobs?.length || 0, draft: 0, active: 0 };

  useEffect(() => {
    if (user) {
      const profileData = user.profiles || user.profile || {};

      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: profileData.phone || "",
        location: profileData.location || "",
        bio: profileData.bio || "",
      });
    }

    console.log("🔍 Current user data:", user);
    console.log("🔍 User profiles:", user?.profiles);
    console.log("🔍 User profile:", user?.profile);
  }, [user]);

  // NEW: Enhanced data fetching with proper loading states
  const loadData = useCallback(
    async (tab: "bookmarks" | "myJobs") => {
      // Prevent concurrent requests
      if (isFetching.current) {
        console.log("⏸️ Fetch already in progress, skipping...");
        return;
      }

      // Rate limiting - minimum 2 seconds between calls
      const now = Date.now();
      if (now - lastFetchTime.current < 2000) {
        console.log("⏸️ Rate limiting data fetch");
        return;
      }

      isFetching.current = true;
      lastFetchTime.current = now;

      // Set loading states
      if (tab === "bookmarks") {
        setLoadingBookmarks(true);
      } else {
        setLoadingJobs(true);
      }

      try {
        console.log(`🔄 Loading ${tab}...`);

        if (tab === "bookmarks") {
          await fetchBookmarks();
          setInitialLoadComplete((prev) => ({ ...prev, bookmarks: true }));
          console.log(
            "✅ Bookmarks loaded from AuthContext:",
            bookmarks?.length || 0,
          );
        } else if (tab === "myJobs") {
          await fetchMyJobs();
          setInitialLoadComplete((prev) => ({ ...prev, myJobs: true }));
          console.log("✅ Jobs loaded from AuthContext:", myJobs?.length || 0);
        }
      } catch (error) {
        console.error(`❌ Failed to load ${tab}:`, error);
        setMessage({
          type: "error",
          text: `Failed to load ${tab}. Please try again.`,
        });
      } finally {
        isFetching.current = false;
        // Clear loading states
        if (tab === "bookmarks") {
          setLoadingBookmarks(false);
        } else {
          setLoadingJobs(false);
        }
      }
    },
    [fetchBookmarks, fetchMyJobs, bookmarks, myJobs],
  );

  // NEW: Debounced tab change handler
  const debouncedTabChange = useCallback(
    debounce((tab: "profile" | "bookmarks" | "myJobs") => {
      setActiveTab(tab);

      // Only fetch data for bookmarks and myJobs tabs
      if (tab === "bookmarks" || tab === "myJobs") {
        loadData(tab);
      }
    }, 300), // 300ms debounce
    [loadData],
  );

  const handleTabChange = (tab: "profile" | "bookmarks" | "myJobs") => {
    // Clear any pending timeouts
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Rate limit tab changes - minimum 1 second between tab changes
    const now = Date.now();
    if (now - lastTabChangeTime.current < 1000) {
      console.log("⏸️ Rate limiting tab change");
      return;
    }

    lastTabChangeTime.current = now;
    debouncedTabChange(tab);
  };

  // NEW: Enhanced useEffect for initial data loading
  useEffect(() => {
    const initialLoad = async () => {
      // Only load data if we're on bookmarks or myJobs tab and haven't loaded recently
      if (activeTab === "bookmarks" || activeTab === "myJobs") {
        const timeSinceLastFetch = Date.now() - lastFetchTime.current;
        if (timeSinceLastFetch > 5000) {
          // Only fetch if more than 5 seconds since last fetch
          await loadData(activeTab);
        }
      }
    };

    initialLoad();
  }, [activeTab, loadData]);

  // NEW: Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  // NEW: Manual refresh function for bookmarks and jobs
  const handleRefreshData = async () => {
    if (activeTab === "bookmarks") {
      await loadData("bookmarks");
    } else if (activeTab === "myJobs") {
      await loadData("myJobs");
    }
  };

  const handleSyncData = async () => {
    if (!syncBookmarksToServer || !syncJobsToServer) {
      setMessage({
        type: "error",
        text: "Sync functionality not available",
      });
      return;
    }

    setSyncingData(true);
    setMessage({ type: "", text: "" });

    try {
      console.log("🔄 Syncing all data to server...");

      const [bookmarksSynced, jobsSynced] = await Promise.all([
        syncBookmarksToServer(),
        syncJobsToServer(),
      ]);

      if (bookmarksSynced && jobsSynced) {
        setMessage({
          type: "success",
          text: "All data synced successfully with server!",
        });

        // Refresh data after sync with delay
        setTimeout(() => {
          if (activeTab === "bookmarks") {
            fetchBookmarks();
          } else if (activeTab === "myJobs") {
            fetchMyJobs();
          }
        }, 1000);
      } else {
        throw new Error("Failed to sync some data");
      }
    } catch (error: any) {
      console.error("Sync error:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to sync data with server",
      });
    } finally {
      setSyncingData(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancelReason.trim()) {
      setMessage({
        type: "error",
        text: "Please provide a reason for cancellation",
      });
      return;
    }

    setCancelingSubscription(true);
    setMessage({ type: "", text: "" });

    try {
      const activeSubscription = getActiveSubscription();
      if (!activeSubscription) {
        throw new Error("No active subscription found");
      }

      // Add null check for subscription ID
      if (!activeSubscription.id) {
        throw new Error("Subscription ID not found");
      }

      // Use apiRequest if available, otherwise fallback to fetch
      if (apiRequest) {
        await apiRequest(`/subscriptions/${activeSubscription.id}`, {
          method: "DELETE",
          body: JSON.stringify({ reason: cancelReason }),
        });
      } else {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE}/subscriptions/${activeSubscription.id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ reason: cancelReason }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to cancel subscription");
        }
      }

      setMessage({
        type: "success",
        text: "Subscription cancelled successfully!",
      });
      setShowCancelSubscription(false);
      setCancelReason("");

      await syncSubscriptionState();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to cancel subscription",
      });
    } finally {
      setCancelingSubscription(false);
    }
  };

  const getActiveSubscription = (): Subscription | null => {
    if (!user?.subscriptionState?.subscriptions?.active?.[0]) return null;
    return user.subscriptionState.subscriptions.active[0];
  };

  const getSubscriptionTypeDisplay = (packageType: string | undefined) => {
    if (!packageType) return "Unknown Subscription";

    switch (packageType) {
      case "job_posting":
        return "Job Posting (Employer)";
      case "job_seeker":
        return "Job Seeker (Candidate)";
      case "whitelist_request":
        return "Whitelist (Unlimited Posts)";
      default:
        return packageType;
    }
  };

  const getSubscriptionIcon = (packageType: string | undefined) => {
    switch (packageType) {
      case "job_posting":
        return <Briefcase size={16} />;
      case "job_seeker":
        return <FileText size={16} />;
      case "whitelist_request":
        return <Zap size={16} />;
      default:
        return <Briefcase size={16} />;
    }
  };

  const handleDeactivateAccount = async () => {
    if (!deactivatePassword.trim()) {
      setMessage({
        type: "error",
        text: "Please enter your password to confirm account deactivation",
      });
      return;
    }

    setMessage({ type: "", text: "" });

    try {
      if (deactivateAccount) {
        await deactivateAccount(deactivatePassword);
        setMessage({
          type: "success",
          text: "Account deactivated successfully. You will be logged out shortly.",
        });
      } else {
        throw new Error("Deactivate account function not available");
      }

      setShowDeactivateAccount(false);
      setDeactivatePassword("");
      setShowDeactivatePassword(false);
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to deactivate account",
      });
    }
  };

  const handleChangePassword = () => {
    navigate("/dashboard/settings?tab=password");
  };

  // NEW: Optimized bookmark removal with rate limiting
  const handleRemoveBookmark = async (jobId: string) => {
    // Prevent rapid consecutive calls
    if (isFetching.current) {
      console.log("⏸️ Operation in progress, please wait...");
      return;
    }

    try {
      console.log("🗑️ Removing bookmark for job:", jobId);

      isFetching.current = true;

      if (removeBookmark) {
        await removeBookmark(jobId);
      } else {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE}/jobs/${jobId}/bookmark`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to remove bookmark");
        }

        // Refresh bookmarks
        await fetchBookmarks();
      }

      setMessage({ type: "success", text: "Bookmark removed successfully!" });

      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
    } catch (error: any) {
      console.error("Error removing bookmark:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to remove bookmark",
      });

      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    } finally {
      isFetching.current = false;
    }
  };

  const handleEditJob = (jobId: string) => {
    setShowJobMenu(null);
    navigate(`/dashboard/edit-job/${jobId}`);
  };

  // NEW: Optimized job deletion with rate limiting
  const handleDeleteJob = async (jobId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this job? This action cannot be undone.",
      )
    ) {
      return;
    }

    // Prevent rapid consecutive calls
    if (isFetching.current) {
      console.log("⏸️ Operation in progress, please wait...");
      return;
    }

    setDeletingJobId(jobId);
    setShowJobMenu(null);
    isFetching.current = true;

    try {
      // Use removeJob from context if available, otherwise fallback
      if (removeJob) {
        await removeJob(jobId);
      } else {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE}/jobs/${jobId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete job");
        }

        // Refresh jobs
        await fetchMyJobs();
      }

      setMessage({ type: "success", text: "Job deleted successfully!" });

      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
    } catch (error: any) {
      console.error("Error deleting job:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to delete job",
      });

      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    } finally {
      setDeletingJobId(null);
      isFetching.current = false;
    }
  };

  const handleViewJob = (jobId: string) => {
    navigate(`/dashboard/find-job`, {
      state: { highlightedJobId: jobId },
    });
  };

  const handlePostJob = () => {
    const subscriptionDetails = getSubscriptionDetails();
    if (subscriptionDetails?.canPostJobs) {
      navigate("/dashboard/create-job");
    } else {
      handleGetSubscription();
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      setMessage({ type: "error", text: "Name and email are required" });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setMessage({ type: "error", text: "Please enter a valid email address" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // Use updateUserProfile if available, otherwise fallback to updateProfile
      const updateFunction = updateUserProfile || updateProfile;
      if (!updateFunction) {
        throw new Error("Update profile function not available");
      }

      const result = await updateFunction({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        location: formData.location.trim(),
        bio: formData.bio.trim(),
      });

      if (result?.success !== false) {
        const emailChanged = formData.email !== user?.email;

        setMessage({
          type: "success",
          text: emailChanged
            ? "Profile updated successfully! Please check your email for verification instructions."
            : result?.message || "Profile updated successfully!",
        });
        setIsEditing(false);
      } else {
        throw new Error(result.message || "Failed to update profile");
      }

      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    } catch (error: any) {
      console.error("Profile update error:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to update profile. Please try again.",
      });

      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleGetSubscription = () => {
    navigate("/dashboard/subscriptions");
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword.trim()) {
      setMessage({
        type: "error",
        text: "Please enter your password to confirm account deletion",
      });
      return;
    }

    setDeletingAccount(true);
    setMessage({ type: "", text: "" });

    try {
      if (deleteAccount) {
        await deleteAccount(deletePassword);
        setMessage({ type: "success", text: "Account deleted successfully" });
      } else {
        throw new Error("Delete account function not available");
      }

      setShowDeleteAccount(false);
      setDeletePassword("");
      setShowDeletePassword(false);
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to delete account",
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleRefreshSubscription = async () => {
    try {
      setLoading(true);
      if (syncSubscriptionState) {
        await syncSubscriptionState();
        setMessage({
          type: "success",
          text: "Subscription status refreshed successfully!",
        });
      } else {
        throw new Error("Sync subscription state function not available");
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to refresh subscription status",
      });
    } finally {
      setLoading(false);
    }
  };

  const getAvatarUrl = () => {
    if (user?.name) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(
        user.name,
      )}&background=0ea5e9&color=fff&size=128`;
    }
    return "https://i.pravatar.cc/150?img=3";
  };

  const getSubscriptionDetails = () => {
    if (!user) return null;

    if (user.subscriptionState) {
      const { capabilities, subscriptions } = user.subscriptionState;
      return {
        canPostJobs: capabilities.canPostJobs,
        canApplyJobs:
          capabilities.canApplyJobs !== undefined
            ? capabilities.canApplyJobs
            : true,
        isWhitelisted: subscriptions.isWhitelisted,
        remainingJobPosts: capabilities.remainingJobPosts,
        maxJobPosts: capabilities.maxJobPosts,
        hasJobPosting: subscriptions.hasJobPosting,
        hasWhitelist: subscriptions.hasWhitelist,
        activeSubscription: subscriptions.active?.[0] || null,
      };
    }

    return {
      canPostJobs: canPostJobs ? canPostJobs() : false,
      canApplyJobs: true,
      isWhitelisted: hasActiveWhitelistSubscription
        ? hasActiveWhitelistSubscription()
        : false,
      remainingJobPosts: getRemainingJobPosts ? getRemainingJobPosts() : 0,
      maxJobPosts: user.jobPostingSubscription?.maxJobPosts || 0,
      hasJobPosting: hasActiveJobPostingSubscription
        ? hasActiveJobPostingSubscription()
        : false,
      hasWhitelist: hasActiveWhitelistSubscription
        ? hasActiveWhitelistSubscription()
        : false,
      activeSubscription: null,
    };
  };

  const subscriptionDetails = getSubscriptionDetails();
  const activeSubscription = getActiveSubscription();

  if (!user) {
    return (
      <div
        className={`min-h-screen py-6 px-4 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <p className={isDark ? "text-gray-300" : "text-gray-600"}>
              Loading user profile...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // NEW: Skeleton for profile loading state
  if (authLoading && activeTab === "profile") {
    return (
      <div
        className={`min-h-screen py-6 px-4 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <div className="max-w-6xl mx-auto">
          {/* Header Skeleton */}
          <div className="mb-6 md:mb-8">
            <Skeleton
              variant="text"
              width="40%"
              animation="pulse"
              theme={isDark ? "dark" : "light"}
              className="h-8 mb-2"
            />
            <Skeleton
              variant="text"
              width="60%"
              animation="pulse"
              theme={isDark ? "dark" : "light"}
              className="h-4"
            />
          </div>

          {/* Tab Navigation Skeleton */}
          <div
            className={`rounded-lg shadow-sm border mb-6 md:mb-8 overflow-hidden ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-sky-100"
            }`}
          >
            <div className="flex">
              {["Profile", "Saved Jobs", "My Jobs"].map((tab) => (
                <div key={tab} className="flex-1 p-4 md:p-6">
                  <Skeleton
                    variant="text"
                    width="80%"
                    animation="pulse"
                    theme={isDark ? "dark" : "light"}
                    className="h-4 mx-auto"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Profile Content Skeleton */}
          <SkeletonDashboard theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-6 px-4 ${
        isDark
          ? "bg-gradient-to-br from-gray-900 to-gray-800"
          : "bg-gradient-to-br from-sky-50 to-blue-50"
      }`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent mb-2">
            My Profile
          </h1>
          <p
            className={`text-sm md:text-base ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Manage your personal information and preferences
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            className={`p-4 mb-6 rounded-lg border ${
              message.type === "success"
                ? isDark
                  ? "bg-green-900/30 text-green-300 border-green-700"
                  : "bg-green-100 text-green-800 border-green-200"
                : isDark
                  ? "bg-red-900/30 text-red-300 border-red-700"
                  : "bg-red-100 text-red-800 border-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Sync Data Button */}
        {typeof syncBookmarksToServer === "function" &&
          typeof syncJobsToServer === "function" && (
            <div className="flex justify-end mb-4">
              <button
                onClick={handleSyncData}
                disabled={syncingData}
                className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50 transition-colors text-sm"
              >
                <RefreshCw
                  size={16}
                  className={syncingData ? "animate-spin" : ""}
                />
                {syncingData ? "Syncing..." : "Sync Data"}
              </button>
            </div>
          )}

        {/* Tab Navigation */}
        <div
          className={`rounded-lg shadow-sm border mb-6 md:mb-8 overflow-hidden ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-sky-100"
          }`}
        >
          <div className="flex overflow-x-auto scrollbar-hide md:overflow-visible">
            <button
              onClick={() => handleTabChange("profile")}
              className={`flex-1 min-w-0 px-4 py-3 md:px-6 md:py-4 font-medium text-sm border-b-2 transition-all whitespace-nowrap ${
                activeTab === "profile"
                  ? isDark
                    ? "border-sky-500 text-sky-400 bg-sky-900/20"
                    : "border-sky-500 text-sky-600 bg-sky-50"
                  : isDark
                    ? "border-transparent text-gray-400 hover:text-gray-300"
                    : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => handleTabChange("bookmarks")}
              className={`flex-1 min-w-0 px-4 py-3 md:px-6 md:py-4 font-medium text-sm border-b-2 transition-all whitespace-nowrap ${
                activeTab === "bookmarks"
                  ? isDark
                    ? "border-sky-500 text-sky-400 bg-sky-900/20"
                    : "border-sky-500 text-sky-600 bg-sky-50"
                  : isDark
                    ? "border-transparent text-gray-400 hover:text-gray-300"
                    : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Saved Jobs ({bookmarkStats.total})
              {bookmarkStats.recent > 0 && (
                <span className="ml-1 text-xs bg-sky-500 text-white px-1 rounded">
                  +{bookmarkStats.recent} new
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange("myJobs")}
              className={`flex-1 min-w-0 px-4 py-3 md:px-6 md:py-4 font-medium text-sm border-b-2 transition-all whitespace-nowrap ${
                activeTab === "myJobs"
                  ? isDark
                    ? "border-sky-500 text-sky-400 bg-sky-900/20"
                    : "border-sky-500 text-sky-600 bg-sky-50"
                  : isDark
                    ? "border-transparent text-gray-400 hover:text-gray-300"
                    : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              My Jobs ({jobStats.total})
              {jobStats.draft > 0 && (
                <span className="ml-1 text-xs bg-orange-500 text-white px-1 rounded">
                  {jobStats.draft} draft
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "profile" ? (
          isEditing ? (
            // Edit Form
            <form
              onSubmit={handleSubmit}
              className={`shadow-lg rounded-xl p-4 md:p-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-sky-100"
              }`}
            >
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                {/* Avatar Section */}
                <div className="lg:w-1/3 flex flex-col items-center">
                  <img
                    src={getAvatarUrl()}
                    alt={user.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg border-4 border-sky-200 mb-4"
                  />
                  <h2
                    className={`text-lg md:text-xl font-semibold text-center ${
                      isDark ? "text-white" : "text-gray-800"
                    }`}
                  >
                    Edit Profile
                  </h2>
                </div>

                {/* Form Fields */}
                <div className="lg:w-2/3 space-y-4 md:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <User size={16} className="inline mr-2" />
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full border rounded-lg px-3 py-2 md:px-4 md:py-3 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition text-sm md:text-base ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "border-sky-200 text-gray-900"
                        }`}
                      />
                    </div>

                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Mail size={16} className="inline mr-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full border rounded-lg px-3 py-2 md:px-4 md:py-3 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition text-sm md:text-base ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "border-sky-200 text-gray-900"
                        }`}
                      />
                    </div>

                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <Phone size={16} className="inline mr-2" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+254 712 345 678"
                        className={`w-full border rounded-lg px-3 py-2 md:px-4 md:py-3 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition text-sm md:text-base ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "border-sky-200 text-gray-900"
                        }`}
                      />
                    </div>

                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <MapPin size={16} className="inline mr-2" />
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="Nairobi, Kenya"
                        className={`w-full border rounded-lg px-3 py-2 md:px-4 md:py-3 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition text-sm md:text-base ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "border-sky-200 text-gray-900"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Bio
                    </label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Tell us about yourself..."
                      className={`w-full border rounded-lg px-3 py-2 md:px-4 md:py-3 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition text-sm md:text-base ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-sky-200 text-gray-900"
                      }`}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      disabled={loading}
                      className={`w-full sm:w-auto px-4 py-2 md:px-6 md:py-3 border rounded-lg font-medium disabled:opacity-50 transition-all duration-300 text-sm md:text-base order-2 sm:order-1 ${
                        isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 transition-all duration-300 shadow-md hover:shadow-lg text-sm md:text-base order-1 sm:order-2"
                    >
                      {loading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            // View Mode
            <div
              className={`shadow-lg rounded-xl p-4 md:p-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-sky-100"
              }`}
            >
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                {/* Left Column - Avatar and Basic Info */}
                <div className="lg:w-1/3 flex flex-col items-center lg:items-start">
                  <img
                    src={getAvatarUrl()}
                    alt={user.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg border-4 border-sky-200 mb-4"
                  />

                  <div className="text-center lg:text-left w-full">
                    <div className="flex flex-col sm:flex-row lg:flex-col justify-between items-center lg:items-start gap-4 mb-6">
                      <div className="text-center lg:text-left">
                        <h2
                          className={`text-xl md:text-2xl font-semibold mb-2 ${
                            isDark ? "text-white" : "text-gray-800"
                          }`}
                        >
                          {user.name}
                        </h2>
                        <p
                          className={`text-sm md:text-base ${
                            isDark ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {(user.profiles || user.profile)?.bio || "No bio yet"}
                        </p>
                      </div>
                      <button
                        onClick={() => setIsEditing(true)}
                        className={`flex items-center justify-center space-x-2 px-4 py-2 border rounded-lg font-medium transition-all duration-300 shadow-sm hover:shadow-md whitespace-nowrap w-full sm:w-auto ${
                          isDark
                            ? "border-sky-500 hover:bg-sky-900/20 bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent"
                            : "border-sky-500 hover:bg-sky-50 bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent"
                        }`}
                      >
                        <Edit2 size={16} />
                        <span>Edit Profile</span>
                      </button>
                    </div>

                    {/* Email Verification Status */}
                    {!user.emailVerified && (
                      <div
                        className={`mb-4 p-3 rounded-lg border ${
                          isDark
                            ? "bg-yellow-900/20 border-yellow-700"
                            : "bg-yellow-50 border-yellow-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <XCircle
                              size={16}
                              className="text-yellow-600 mr-2"
                            />
                            <span
                              className={`text-sm font-medium ${
                                isDark ? "text-yellow-300" : "text-yellow-800"
                              }`}
                            >
                              Email not verified
                            </span>
                          </div>
                          <button
                            onClick={() => setShowVerificationModal(true)}
                            className="text-xs bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600 transition-colors flex items-center gap-1"
                          >
                            <Send size={12} />
                            Verify Email
                          </button>
                        </div>
                        <p
                          className={`text-xs mt-2 ${isDark ? "text-yellow-400" : "text-yellow-700"}`}
                        >
                          Please verify your email to access all features
                        </p>
                      </div>
                    )}

                    {user.emailVerified && (
                      <div
                        className={`mb-4 p-3 rounded-lg border ${
                          isDark
                            ? "bg-green-900/20 border-green-700"
                            : "bg-green-50 border-green-200"
                        }`}
                      >
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-green-600 mr-2"
                          />
                          <span
                            className={`text-sm font-medium ${
                              isDark ? "text-green-300" : "text-green-800"
                            }`}
                          >
                            Email verified
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6">
                      <div
                        className={`text-center p-3 rounded-lg ${
                          isDark ? "bg-sky-900/20" : "bg-sky-50"
                        }`}
                      >
                        <p className="text-xl md:text-2xl font-bold text-sky-600">
                          0
                        </p>
                        <p
                          className={`text-xs md:text-sm ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Jobs Applied
                        </p>
                      </div>
                      <div
                        className={`text-center p-3 rounded-lg ${
                          isDark ? "bg-sky-900/20" : "bg-sky-50"
                        }`}
                      >
                        <p className="text-xl md:text-2xl font-bold text-sky-600">
                          {bookmarks?.length || 0}
                        </p>
                        <p
                          className={`text-xs md:text-sm ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Saved Jobs
                        </p>
                      </div>
                      <div
                        className={`text-center p-3 rounded-lg ${
                          isDark ? "bg-sky-900/20" : "bg-sky-50"
                        }`}
                      >
                        <p className="text-xl md:text-2xl font-bold text-sky-600">
                          {user.subscribed ? "Active" : "Free"}
                        </p>
                        <p
                          className={`text-xs md:text-sm ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Subscription
                        </p>
                      </div>
                      <div
                        className={`text-center p-3 rounded-lg ${
                          isDark ? "bg-sky-900/20" : "bg-sky-50"
                        }`}
                      >
                        <p className="text-lg md:text-xl font-bold text-sky-600">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                        <p
                          className={`text-xs md:text-sm ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Member Since
                        </p>
                      </div>
                    </div>

                    {/* Account Management Section */}
                    <div
                      className={`mt-6 pt-6 border-t ${
                        isDark ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <h3 className="text-lg font-semibold text-sky-600 mb-3 flex items-center">
                        <Shield size={18} className="mr-2" />
                        Account Security
                      </h3>

                      <button
                        onClick={handleChangePassword}
                        className="w-full px-4 py-2 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors flex items-center justify-center gap-2 mb-4"
                      >
                        <Shield size={16} />
                        Change Password
                      </button>

                      <h3 className="text-lg font-semibold text-red-600 mb-3 flex items-center">
                        <AlertTriangle size={18} className="mr-2" />
                        Account Management
                      </h3>

                      {/* Deactivate Account */}
                      <div className="mb-4">
                        <p
                          className={`text-sm mb-2 ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Temporarily disable your account. You can reactivate
                          it later by logging in.
                        </p>
                        <button
                          onClick={() => setShowDeactivateAccount(true)}
                          className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 mb-2"
                        >
                          <ShieldOff size={16} />
                          Deactivate Account
                        </button>
                      </div>

                      {/* Delete Account */}
                      <div>
                        <p
                          className={`text-sm mb-2 ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Permanently delete your account and all data. This
                          action cannot be undone.
                        </p>
                        <button
                          onClick={() => setShowDeleteAccount(true)}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Trash2 size={16} />
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Contact and Subscription Details */}
                <div className="lg:w-2/3 space-y-4 md:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="space-y-3 md:space-y-4">
                      <div
                        className={`flex items-center space-x-3 p-3 rounded-lg ${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        }`}
                      >
                        <Mail
                          size={18}
                          className="text-sky-500 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p
                            className={`text-xs ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Email
                          </p>
                          <p
                            className={`font-medium text-sm md:text-base truncate ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {user.email}
                          </p>
                        </div>
                      </div>

                      <div
                        className={`flex items-center space-x-3 p-3 rounded-lg ${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        }`}
                      >
                        <Phone
                          size={18}
                          className="text-sky-500 flex-shrink-0"
                        />
                        <div>
                          <p
                            className={`text-xs ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Phone
                          </p>
                          <p
                            className={`font-medium text-sm md:text-base ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {(user.profiles || user.profile)?.phone ||
                              "Not provided"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 md:space-y-4">
                      <div
                        className={`flex items-center space-x-3 p-3 rounded-lg ${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        }`}
                      >
                        <Briefcase
                          size={18}
                          className="text-sky-500 flex-shrink-0"
                        />
                        <div>
                          <p
                            className={`text-xs ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Role
                          </p>
                          <p className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent font-medium capitalize text-sm md:text-base">
                            {user.role}
                            {user.isWhitelisted && " • Whitelisted"}
                          </p>
                        </div>
                      </div>

                      <div
                        className={`flex items-center space-x-3 p-3 rounded-lg ${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        }`}
                      >
                        <MapPin
                          size={18}
                          className="text-sky-500 flex-shrink-0"
                        />
                        <div>
                          <p
                            className={`text-xs ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Location
                          </p>
                          <p
                            className={`font-medium text-sm md:text-base ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {(user.profiles || user.profile)?.location ||
                              "Not specified"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Subscription Status */}
                  <div
                    className={`rounded-lg p-3 md:p-4 border ${
                      isDark
                        ? "bg-sky-900/20 border-sky-800"
                        : "bg-sky-50 border-sky-200"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3
                        className={`font-semibold text-sm md:text-base ${
                          isDark ? "text-sky-300" : "text-sky-800"
                        }`}
                      >
                        Subscription Status
                      </h3>
                      <button
                        onClick={handleRefreshSubscription}
                        disabled={loading}
                        className="text-xs bg-sky-500 text-white px-2 py-1 rounded hover:bg-sky-600 disabled:opacity-50 transition-colors"
                      >
                        {loading ? "Refreshing..." : "Refresh"}
                      </button>
                    </div>

                    <div className="space-y-3">
                      {/* Active Subscription Display */}
                      {activeSubscription ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            {getSubscriptionIcon(
                              activeSubscription.packageType,
                            )}
                            <p
                              className={`text-sm md:text-base font-medium ${
                                isDark ? "text-sky-300" : "text-sky-700"
                              }`}
                            >
                              {getSubscriptionTypeDisplay(
                                activeSubscription.packageType,
                              )}{" "}
                              - {activeSubscription.package?.name}
                            </p>
                          </div>

                          {/* Subscription Details */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            <div className="flex justify-between">
                              <span
                                className={
                                  isDark ? "text-gray-400" : "text-gray-600"
                                }
                              >
                                Status:
                              </span>
                              <span
                                className={
                                  activeSubscription.status === "active"
                                    ? "text-green-500"
                                    : activeSubscription.status === "canceled"
                                      ? "text-orange-500"
                                      : "text-red-500"
                                }
                              >
                                {activeSubscription.status
                                  .charAt(0)
                                  .toUpperCase() +
                                  activeSubscription.status.slice(1)}
                              </span>
                            </div>

                            <div className="flex justify-between">
                              <span
                                className={
                                  isDark ? "text-gray-400" : "text-gray-600"
                                }
                              >
                                Plan:
                              </span>
                              <span
                                className={
                                  isDark ? "text-sky-300" : "text-sky-600"
                                }
                              >
                                {activeSubscription.planCode}
                              </span>
                            </div>
                          </div>

                          {/* Expiration Date */}
                          <div className="flex items-center gap-2 text-xs">
                            <Calendar
                              size={12}
                              className={
                                isDark ? "text-sky-400" : "text-sky-600"
                              }
                            />
                            <span
                              className={
                                isDark ? "text-sky-400" : "text-sky-600"
                              }
                            >
                              {activeSubscription.status === "active"
                                ? `Expires: ${new Date(activeSubscription.endDate).toLocaleDateString()}`
                                : `Ended: ${new Date(activeSubscription.endDate).toLocaleDateString()}`}
                            </span>
                          </div>

                          {/* Package-specific details */}
                          {activeSubscription.packageType === "job_seeker" && (
                            <div className="text-xs space-y-1">
                              <p
                                className={
                                  isDark ? "text-gray-400" : "text-gray-600"
                                }
                              >
                                Features included:
                              </p>
                              <ul className="list-disc list-inside space-y-1">
                                {activeSubscription.features?.map(
                                  (feature: string, index: number) => (
                                    <li
                                      key={index}
                                      className={
                                        isDark
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }
                                    >
                                      {feature}
                                    </li>
                                  ),
                                )}
                              </ul>
                              <p className="text-green-500 font-medium mt-1">
                                ✓ Can apply to jobs
                              </p>
                            </div>
                          )}

                          {activeSubscription.packageType === "job_posting" &&
                            subscriptionDetails && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                <div className="flex justify-between">
                                  <span
                                    className={
                                      isDark ? "text-gray-400" : "text-gray-600"
                                    }
                                  >
                                    Job Posts Remaining:
                                  </span>
                                  <span
                                    className={
                                      isDark ? "text-sky-300" : "text-sky-600"
                                    }
                                  >
                                    {subscriptionDetails.remainingJobPosts} /{" "}
                                    {subscriptionDetails.maxJobPosts ||
                                      "Unlimited"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span
                                    className={
                                      isDark ? "text-gray-400" : "text-gray-600"
                                    }
                                  >
                                    Can Post Jobs:
                                  </span>
                                  <span className="text-green-500">✓ Yes</span>
                                </div>
                              </div>
                            )}

                          {activeSubscription.packageType ===
                            "whitelist_request" && (
                            <div className="text-xs space-y-1">
                              <p className="text-green-500 font-medium">
                                ✓ Unlimited job posting access
                              </p>
                              <p
                                className={
                                  isDark ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                Premium whitelisted member
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p
                          className={`text-sm md:text-base ${isDark ? "text-sky-300" : "text-sky-700"}`}
                        >
                          No active subscription
                        </p>
                      )}

                      {/* Capabilities Summary */}
                      {subscriptionDetails && (
                        <div className="pt-2 border-t border-sky-200 dark:border-sky-700">
                          <p className="text-xs font-medium mb-1 text-sky-600 dark:text-sky-400">
                            Your Capabilities:
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                            <div className="flex items-center gap-1">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  subscriptionDetails.canPostJobs
                                    ? "bg-green-500"
                                    : "bg-gray-400"
                                }`}
                              />
                              <span
                                className={
                                  isDark ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                Post Jobs
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  (
                                    subscriptionDetails.canApplyJobs !==
                                    undefined
                                      ? subscriptionDetails.canApplyJobs
                                      : true
                                  )
                                    ? "bg-green-500"
                                    : "bg-gray-400"
                                }`}
                              />
                              <span
                                className={
                                  isDark ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                Apply to Jobs
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  subscriptionDetails.isWhitelisted
                                    ? "bg-green-500"
                                    : "bg-gray-400"
                                }`}
                              />
                              <span
                                className={
                                  isDark ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                Whitelisted
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      {!activeSubscription ? (
                        <button
                          onClick={handleGetSubscription}
                          className="px-3 py-1 md:px-4 md:py-2 bg-sky-500 text-white rounded-lg text-sm font-medium hover:bg-sky-600 transition-colors whitespace-nowrap w-full"
                        >
                          Get Subscription
                        </button>
                      ) : (
                        activeSubscription.status === "active" && (
                          <button
                            onClick={() => setShowCancelSubscription(true)}
                            className="px-3 py-1 md:px-4 md:py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors whitespace-nowrap w-full"
                          >
                            Cancel Subscription
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : activeTab === "bookmarks" ? (
          // Enhanced Bookmarks Tab with Skeleton Loading
          <div
            className={`shadow-lg rounded-xl p-4 md:p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-sky-100"
            }`}
          >
            <div className="flex justify-between items-center mb-4 md:mb-6">
              <h2
                className={`text-xl md:text-2xl font-semibold flex items-center ${
                  isDark ? "text-white" : "text-gray-800"
                }`}
              >
                <Bookmark size={20} className="text-sky-500 mr-2 md:mr-3" />
                Saved Jobs ({bookmarkStats.total})
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefreshData}
                  disabled={loadingBookmarks}
                  className="flex items-center gap-2 px-3 py-1 bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50 transition-colors text-sm"
                >
                  <RefreshCw
                    size={14}
                    className={loadingBookmarks ? "animate-spin" : ""}
                  />
                  {loadingBookmarks ? "Loading..." : "Refresh"}
                </button>
                {bookmarkStats.total > 0 && clearAllBookmarks && (
                  <button
                    onClick={() => {
                      if (
                        confirm("Are you sure you want to clear all bookmarks?")
                      ) {
                        clearAllBookmarks();
                        setMessage({
                          type: "success",
                          text: "All bookmarks cleared!",
                        });
                      }
                    }}
                    className="px-3 py-1 text-red-500 border border-red-500 rounded-lg hover:bg-red-50 transition-colors text-sm"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            {loadingBookmarks && !initialLoadComplete.bookmarks ? (
              // Bookmarks Skeleton Loading State
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      isDark
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-3">
                          <Skeleton
                            variant="text"
                            width="70%"
                            animation="pulse"
                            theme={isDark ? "dark" : "light"}
                            className="h-6 mb-2"
                          />
                          <Skeleton
                            variant="circle"
                            width={20}
                            height={20}
                            animation="pulse"
                            theme={isDark ? "dark" : "light"}
                          />
                        </div>
                        <Skeleton
                          variant="text"
                          width="50%"
                          animation="pulse"
                          theme={isDark ? "dark" : "light"}
                          className="h-4 mb-3"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <Skeleton
                            variant="badge"
                            width={80}
                            animation="pulse"
                            theme={isDark ? "dark" : "light"}
                            className="h-6"
                          />
                          <Skeleton
                            variant="badge"
                            width={100}
                            animation="pulse"
                            theme={isDark ? "dark" : "light"}
                            className="h-6"
                          />
                          <Skeleton
                            variant="badge"
                            width={120}
                            animation="pulse"
                            theme={isDark ? "dark" : "light"}
                            className="h-6"
                          />
                        </div>
                      </div>
                      <Skeleton
                        variant="circle"
                        width={24}
                        height={24}
                        animation="pulse"
                        theme={isDark ? "dark" : "light"}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : !bookmarks || bookmarks.length === 0 ? (
              <div className="text-center py-8 md:py-12">
                <Bookmark
                  size={40}
                  className={`mx-auto mb-3 md:mb-4 ${
                    isDark ? "text-gray-600" : "text-gray-300"
                  }`}
                />
                <p
                  className={`mb-2 text-base md:text-lg ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  No saved jobs yet
                </p>
                <p
                  className={`text-sm md:text-base ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Start browsing jobs and save them for later!
                </p>
                <button
                  onClick={() => navigate("/dashboard/find-job")}
                  className="mt-4 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
                >
                  Browse Jobs
                </button>
              </div>
            ) : (
              <div className="grid gap-3 md:gap-4">
                {bookmarks.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className={`border rounded-lg p-3 md:p-4 hover:shadow-md transition-all ${
                      isDark
                        ? "border-gray-700 hover:border-gray-600"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2 md:gap-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <h3
                            className={`font-semibold text-base md:text-lg line-clamp-2 ${
                              isDark ? "text-white" : "text-gray-800"
                            }`}
                          >
                            {bookmark.job.title}
                          </h3>
                          <button
                            onClick={() => handleViewJob(bookmark.job.id)}
                            className="flex-shrink-0 ml-2 p-1 text-sky-500 hover:text-sky-600 transition-colors"
                            title="View job details"
                          >
                            <ExternalLink size={16} />
                          </button>
                        </div>
                        <p
                          className={`font-medium text-sm md:text-base mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {bookmark.job.company}
                        </p>
                        <div className="flex flex-wrap items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <MapPin size={12} className="flex-shrink-0" />
                            <span className="truncate">
                              {bookmark.job.location}
                            </span>
                          </div>
                          <span>•</span>
                          <span className="capitalize">
                            {bookmark.job.type.replace("_", " ")}
                          </span>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Calendar size={12} className="flex-shrink-0" />
                            <span>
                              Saved{" "}
                              {new Date(
                                bookmark.createdAt,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveBookmark(bookmark.job.id)}
                        className={`flex-shrink-0 p-1 md:p-2 text-red-500 rounded-lg transition-colors ml-2 ${
                          isDark ? "hover:bg-red-900/30" : "hover:bg-red-50"
                        }`}
                        title="Remove bookmark"
                      >
                        <Bookmark
                          size={16}
                          className="fill-current md:size-5"
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Enhanced My Jobs Tab with Skeleton Loading
          <div
            className={`shadow-lg rounded-xl p-4 md:p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-sky-100"
            }`}
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
              <div>
                <h2
                  className={`text-xl md:text-2xl font-semibold flex items-center ${
                    isDark ? "text-white" : "text-gray-800"
                  }`}
                >
                  <BriefcaseIcon
                    size={20}
                    className="text-sky-500 mr-2 md:mr-3"
                  />
                  My Jobs ({jobStats.total})
                </h2>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className={isDark ? "text-gray-400" : "text-gray-600"}>
                    Active: {jobStats.active}
                  </span>
                  <span className={isDark ? "text-gray-400" : "text-gray-600"}>
                    Drafts: {jobStats.draft}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleRefreshData}
                  disabled={loadingJobs}
                  className="flex items-center gap-2 px-3 py-1 bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50 transition-colors text-sm"
                >
                  <RefreshCw
                    size={14}
                    className={loadingJobs ? "animate-spin" : ""}
                  />
                  {loadingJobs ? "Loading..." : "Refresh"}
                </button>
                {myJobs && myJobs.length > 0 && clearAllJobs && (
                  <button
                    onClick={() => {
                      if (confirm("Are you sure you want to clear all jobs?")) {
                        clearAllJobs();
                        setMessage({
                          type: "success",
                          text: "All jobs cleared!",
                        });
                      }
                    }}
                    className="px-3 py-1 text-red-500 border border-red-500 rounded-lg hover:bg-red-50 transition-colors text-sm"
                  >
                    Clear All
                  </button>
                )}
                {subscriptionDetails?.canPostJobs && (
                  <button
                    onClick={handlePostJob}
                    className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors shadow-md hover:shadow-lg text-sm"
                  >
                    Post New Job
                  </button>
                )}
              </div>
            </div>

            {loadingJobs && !initialLoadComplete.myJobs ? (
              // My Jobs Skeleton Loading State
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      isDark
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                      {/* Job Info Skeleton */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2 mb-3">
                          <div className="flex-1">
                            <Skeleton
                              variant="text"
                              width="80%"
                              animation="pulse"
                              theme={isDark ? "dark" : "light"}
                              className="h-6 mb-2"
                            />
                            <Skeleton
                              variant="text"
                              width="60%"
                              animation="pulse"
                              theme={isDark ? "dark" : "light"}
                              className="h-4 mb-3"
                            />
                          </div>
                          <Skeleton
                            variant="circle"
                            width={24}
                            height={24}
                            animation="pulse"
                            theme={isDark ? "dark" : "light"}
                            className="sm:hidden"
                          />
                        </div>

                        {/* Job Details Skeleton */}
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Skeleton
                              variant="badge"
                              width={100}
                              animation="pulse"
                              theme={isDark ? "dark" : "light"}
                              className="h-6"
                            />
                            <Skeleton
                              variant="badge"
                              width={80}
                              animation="pulse"
                              theme={isDark ? "dark" : "light"}
                              className="h-6"
                            />
                          </div>

                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <Skeleton
                              variant="text"
                              width={120}
                              animation="pulse"
                              theme={isDark ? "dark" : "light"}
                              className="h-4"
                            />
                            <Skeleton
                              variant="text"
                              width={100}
                              animation="pulse"
                              theme={isDark ? "dark" : "light"}
                              className="h-4"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Action Menu Skeleton */}
                      <div className="hidden sm:block">
                        <div className="flex gap-2">
                          {[1, 2, 3].map((btn) => (
                            <Skeleton
                              key={btn}
                              variant="button"
                              width={60}
                              animation="pulse"
                              theme={isDark ? "dark" : "light"}
                              className="h-8"
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Mobile Action Menu Skeleton */}
                    <div className="sm:hidden mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex gap-2">
                        {[1, 2, 3].map((btn) => (
                          <Skeleton
                            key={btn}
                            variant="button"
                            width="100%"
                            animation="pulse"
                            theme={isDark ? "dark" : "light"}
                            className="h-10"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : !myJobs || myJobs.length === 0 ? (
              <div className="text-center py-8 md:py-12">
                <BriefcaseIcon
                  size={40}
                  className={`mx-auto mb-3 md:mb-4 ${
                    isDark ? "text-gray-600" : "text-gray-300"
                  }`}
                />
                <p
                  className={`mb-2 text-base md:text-lg ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  No jobs posted yet
                </p>
                <p
                  className={`text-sm md:text-base mb-4 md:mb-6 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {subscriptionDetails?.canPostJobs
                    ? "Start posting jobs to find the perfect candidates!"
                    : "Get a subscription to start posting jobs"}
                </p>
                <button
                  onClick={handlePostJob}
                  className="px-4 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 transition-colors"
                >
                  {subscriptionDetails?.canPostJobs
                    ? "Post Your First Job"
                    : "Get Subscription"}
                </button>
              </div>
            ) : (
              <div className="space-y-3 md:space-y-4">
                {myJobs.map((job) => (
                  <div
                    key={job.id}
                    className={`border rounded-lg p-3 md:p-4 hover:shadow-md transition-all group ${
                      isDark
                        ? "border-gray-700 hover:border-gray-600"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      {/* Job Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <h3
                            className={`font-semibold text-base md:text-lg line-clamp-2 flex-1 ${
                              isDark ? "text-white" : "text-gray-800"
                            }`}
                          >
                            {job.job?.title || job.title}
                          </h3>
                          {/* Mobile Action Button */}
                          <div className="sm:hidden">
                            <button
                              onClick={() =>
                                setShowJobMenu(
                                  showJobMenu === job.id ? null : job.id,
                                )
                              }
                              className={`p-1 rounded-lg transition-colors ${
                                isDark
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              <MoreVertical size={16} />
                            </button>
                          </div>
                        </div>

                        <p
                          className={`font-medium text-sm md:text-base mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {job.job?.company || job.company}
                        </p>

                        {/* Job Details */}
                        <div className="space-y-2 md:space-y-0">
                          <div className="flex flex-wrap items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <MapPin size={12} className="flex-shrink-0" />
                              <span className="truncate">
                                {job.job?.location || job.location}
                              </span>
                            </div>
                            <span className="hidden md:inline">•</span>
                            <div className="flex items-center gap-1">
                              <BriefcaseIcon
                                size={12}
                                className="flex-shrink-0"
                              />
                              <span className="capitalize">
                                {(job.job?.type || job.type)?.replace("_", " ")}
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <Users size={12} className="flex-shrink-0" />
                              <span>
                                {job.job?._count?.jobApplications ||
                                  job._count?.jobApplications ||
                                  0}{" "}
                                application
                                {job.job?._count?.jobApplications !== 1
                                  ? "s"
                                  : ""}
                              </span>
                            </div>
                            <span className="hidden md:inline">•</span>
                            <div className="flex items-center gap-1">
                              <Calendar size={12} className="flex-shrink-0" />
                              <span>
                                {new Date(job.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Desktop Action Menu */}
                      <div className="hidden sm:block relative flex-shrink-0">
                        <button
                          onClick={() =>
                            setShowJobMenu(
                              showJobMenu === job.id ? null : job.id,
                            )
                          }
                          className={`p-2 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"
                          }`}
                        >
                          <MoreVertical size={16} />
                        </button>

                        {showJobMenu === job.id && (
                          <div
                            className={`absolute right-0 top-8 border rounded-lg shadow-lg z-10 min-w-32 ${
                              isDark
                                ? "bg-gray-800 border-gray-700"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            <button
                              onClick={() => handleViewJob(job.id)}
                              className={`w-full px-4 py-2 text-left text-sm border-b flex items-center gap-2 ${
                                isDark
                                  ? "text-sky-400 hover:bg-sky-900/20 border-gray-700"
                                  : "text-sky-600 hover:bg-sky-50 border-gray-100"
                              }`}
                            >
                              <ExternalLink size={14} />
                              View
                            </button>
                            <button
                              onClick={() => handleEditJob(job.id)}
                              className={`w-full px-4 py-2 text-left text-sm border-b flex items-center gap-2 ${
                                isDark
                                  ? "text-sky-400 hover:bg-sky-900/20 border-gray-700"
                                  : "text-sky-600 hover:bg-sky-50 border-gray-100"
                              }`}
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteJob(job.id)}
                              disabled={deletingJobId === job.id}
                              className={`w-full px-4 py-2 text-left text-sm disabled:opacity-50 flex items-center gap-2 ${
                                isDark
                                  ? "text-red-400 hover:bg-red-900/20"
                                  : "text-red-600 hover:bg-red-50"
                              }`}
                            >
                              {deletingJobId === job.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 size={14} />
                                  Delete
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Mobile Action Menu */}
                    {showJobMenu === job.id && (
                      <div
                        className={`sm:hidden mt-3 pt-3 border-t ${
                          isDark ? "border-gray-700" : "border-gray-200"
                        }`}
                      >
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewJob(job.id)}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg transition-colors text-center ${
                              isDark
                                ? "text-sky-400 border-sky-600 hover:bg-sky-900/20"
                                : "text-sky-600 border-sky-600 hover:bg-sky-50"
                            }`}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEditJob(job.id)}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg transition-colors text-center ${
                              isDark
                                ? "text-sky-400 border-sky-600 hover:bg-sky-900/20"
                                : "text-sky-600 border-sky-600 hover:bg-sky-50"
                            }`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteJob(job.id)}
                            disabled={deletingJobId === job.id}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg transition-colors text-center ${
                              isDark
                                ? "text-red-400 border-red-600 hover:bg-red-900/20 disabled:opacity-50"
                                : "text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50"
                            }`}
                          >
                            {deletingJobId === job.id
                              ? "Deleting..."
                              : "Delete"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        {/* Delete Account Modal */}
        {showDeleteAccount && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
              className={`rounded-xl p-6 max-w-md w-full ${
                isDark ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div className="flex items-center mb-4">
                <AlertTriangle className="text-red-500 mr-3" size={24} />
                <h3 className="text-lg font-semibold text-red-600">
                  Delete Account
                </h3>
              </div>

              <p
                className={`mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}
              >
                This action cannot be undone. This will permanently delete your
                account and remove all your data from our servers.
              </p>

              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Enter your password to confirm:
                </label>
                <div className="relative">
                  <input
                    type={showDeletePassword ? "text" : "password"}
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-gray-300 text-gray-900"
                    }`}
                    placeholder="Your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowDeletePassword(!showDeletePassword)}
                    className={`absolute inset-y-0 right-0 pr-3 flex items-center ${
                      isDark
                        ? "text-gray-400 hover:text-gray-300"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {showDeletePassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteAccount(false);
                    setDeletePassword("");
                    setShowDeletePassword(false);
                  }}
                  className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                  disabled={deletingAccount || authLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={
                    deletingAccount || !deletePassword.trim() || authLoading
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {deletingAccount || authLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deactivate Account Modal */}
        {showDeactivateAccount && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
              className={`rounded-xl p-6 max-w-md w-full ${
                isDark ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div className="flex items-center mb-4">
                <ShieldOff className="text-orange-500 mr-3" size={24} />
                <h3 className="text-lg font-semibold text-orange-600">
                  Deactivate Account
                </h3>
              </div>

              <p
                className={`mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}
              >
                Your account will be temporarily disabled. You can reactivate it
                at any time by logging in. Your data will be preserved.
              </p>

              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Enter your password to confirm:
                </label>
                <div className="relative">
                  <input
                    type={showDeactivatePassword ? "text" : "password"}
                    value={deactivatePassword}
                    onChange={(e) => setDeactivatePassword(e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-gray-300 text-gray-900"
                    }`}
                    placeholder="Your password"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowDeactivatePassword(!showDeactivatePassword)
                    }
                    className={`absolute inset-y-0 right-0 pr-3 flex items-center ${
                      isDark
                        ? "text-gray-400 hover:text-gray-300"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {showDeactivatePassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeactivateAccount(false);
                    setDeactivatePassword("");
                    setShowDeactivatePassword(false);
                  }}
                  className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                  disabled={authLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivateAccount}
                  disabled={!deactivatePassword.trim() || authLoading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {authLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deactivating...
                    </>
                  ) : (
                    <>
                      <ShieldOff size={16} />
                      Deactivate Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Subscription Modal */}
        {showCancelSubscription && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
              className={`rounded-xl p-6 max-w-md w-full ${
                isDark ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div className="flex items-center mb-4">
                <AlertCircle className="text-orange-500 mr-3" size={24} />
                <h3 className="text-lg font-semibold text-orange-600">
                  Cancel Subscription
                </h3>
              </div>

              <p
                className={`mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}
              >
                Are you sure you want to cancel your subscription? You'll be
                able to use your remaining features until{" "}
                {activeSubscription?.endDate
                  ? new Date(activeSubscription.endDate).toLocaleDateString()
                  : "the end of your billing period"}
                .
              </p>

              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Reason for cancellation (optional):
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  placeholder="Help us improve by sharing your reason..."
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowCancelSubscription(false);
                    setCancelReason("");
                  }}
                  className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                  disabled={cancelingSubscription}
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelingSubscription}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {cancelingSubscription ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Canceling...
                    </>
                  ) : (
                    <>
                      <X size={16} />
                      Cancel Subscription
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <ResendVerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          currentEmail={user.email}
        />
      </div>
    </div>
  );
}
